import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { chat } from "@/lib/api";

function makeSessionId() {
  return (
    "chat-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36)
  );
}

export default function ChatBot() {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(makeSessionId());
  const scrollRef = useRef(null);

  // Reset welcome when language changes
  useEffect(() => {
    setMessages([{ role: "assistant", text: t("chat_welcome") }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await chat({
        message: text,
        session_id: sessionRef.current,
        language: lang,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            lang === "en"
              ? "Sorry, I'm having trouble right now. Please try again."
              : "Beklager, jeg har problemer akkurat nå. Prøv igjen.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          data-testid="chat-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-[60] bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all hover:scale-105 group"
          aria-label="Open chat"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
          <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-[#D97B66] rounded-full slow-pulse"></span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] bg-white border border-[#E5E2DC] rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#2C302E] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#8BA896] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-heading text-sm font-medium">Småstund</p>
                <p className="text-[10px] text-white/60 uppercase tracking-widest">
                  {lang === "en" ? "Always here" : "Alltid her"}
                </p>
              </div>
            </div>
            <button
              data-testid="chat-close-button"
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-6 space-y-4 bg-[#F9F8F6]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#8BA896] text-white rounded-br-sm"
                      : "bg-white text-[#2C302E] border border-[#E5E2DC] rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-[#6B726C] border border-[#E5E2DC] rounded-2xl rounded-bl-sm px-4 py-3 text-sm flex gap-1">
                  <span className="w-1.5 h-1.5 bg-[#6B726C] rounded-full animate-bounce"></span>
                  <span className="w-1.5 h-1.5 bg-[#6B726C] rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}></span>
                  <span className="w-1.5 h-1.5 bg-[#6B726C] rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}></span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={send}
            className="border-t border-[#E5E2DC] bg-white px-4 py-3 flex items-center gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat_placeholder")}
              className="flex-1 px-4 py-2.5 bg-[#F9F8F6] rounded-full text-sm text-[#2C302E] placeholder:text-[#6B726C] focus:outline-none border border-transparent focus:border-[#8BA896]"
            />
            <button
              type="submit"
              data-testid="chat-send-button"
              disabled={loading || !input.trim()}
              className="bg-[#2C302E] hover:bg-[#1f221f] disabled:opacity-40 text-white rounded-full p-2.5 transition-colors"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
